// =======================
// src/paginas/modelos/componentes/catalogoModelosLista.js
// =======================

import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ArrowDownward, ArrowUpward, UnfoldMore } from "@mui/icons-material";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";

import { separadorPaginacao } from "../../../estilos/paginacao";
import { coresTextosGlobais, tamanhosPaginasAdministrativas, ajustarTamanhoFonteSre } from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const GAP_CHIPS_PX = 5;

/*
 * =========================================================
 * DISTRIBUIÇÃO DAS COLUNAS
 * =========================================================
 *
 * SRE:
 * Nome       25%
 * Categoria  18%
 * Assunto    22%
 * Etapa      25%
 * Ações      10%
 */
const GRID_COLUNAS_SRE = "25% 18% 22% 25% 10%";

/*
 * Analista / Escola:
 *
 * Nome       25%
 * Categoria  18%
 * Assunto    24%
 * Etapa      26%
 * Baixar      7%
 */
const GRID_COLUNAS_CONSULTA = "25% 18% 24% 26% 7%";

/*
 * =========================================================
 * ESTILOS DA LISTA
 * =========================================================
 */

const ESTILO_TEXTO_DUAS_LINHAS = {
  minWidth: 0,

  lineHeight: 1.35,

  display: "-webkit-box",

  WebkitLineClamp: 2,

  WebkitBoxOrient: "vertical",

  overflow: "hidden",

  overflowWrap: "break-word",
};

const ESTILO_CHIP_ETAPA = {
  minHeight: 23,

  height: 23,

  maxWidth: "100%",

  backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",

  color: "var(--sear-azul, #0767d8)",

  fontSize: ajustarTamanhoFonteSre("0.7rem"),

  "& .MuiChip-label": {
    px: 1,

    whiteSpace: "nowrap",

    lineHeight: 1.2,
  },
};

/*
 * =========================================================
 * ESTILOS DAS AÇÕES
 * =========================================================
 */

const criarEstiloAcao = ({
  cor,
  fundo,
  borda,
  corHover,
  fundoHover,
  sombraHover,
}) => ({
  width: 28,

  height: 28,

  p: 0,

  borderRadius: 1.2,

  color: cor,

  backgroundColor: fundo,

  border: `1px solid ${borda}`,

  transition:
    "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",

  "&:hover": {
    color: corHover,

    backgroundColor: fundoHover,

    borderColor: corHover,

    transform: "translateY(-1px)",

    boxShadow: sombraHover,
  },
});

const ESTILO_ACAO_DOWNLOAD = criarEstiloAcao({
  cor: "var(--sear-azul, #00897b)",

  fundo: "var(--sear-sucesso-fundo, #e8f7f4)",

  borda: "var(--sear-sucesso-borda, #ccece6)",

  corHover: "var(--sear-sucesso-texto, #00695c)",

  fundoHover: "var(--sear-sucesso-fundo, #d5f0eb)",

  sombraHover: "0 2px 5px rgba(0, 105, 92, 0.18)",
});

const ESTILO_ACAO_EDITAR = criarEstiloAcao({
  cor: "var(--sear-azul, #1976d2)",

  fundo: "var(--sear-superficie-azul, #eaf3ff)",

  borda: "var(--sear-borda, #d0e5fb)",

  corHover: "var(--sear-azul, #0d5db8)",

  fundoHover: "var(--sear-superficie-azul, #dbeafe)",

  sombraHover: "0 2px 5px rgba(25, 118, 210, 0.18)",
});

const ESTILO_ACAO_EXCLUIR = criarEstiloAcao({
  cor: "var(--sear-erro-texto, #d32f2f)",

  fundo: "var(--sear-erro-fundo, #fff0f0)",

  borda: "var(--sear-erro-borda, #f8d7d7)",

  corHover: "var(--sear-erro-texto, #b71c1c)",

  fundoHover: "var(--sear-erro-fundo, #ffe1e1)",

  sombraHover: "0 2px 5px rgba(211, 47, 47, 0.18)",
});

/*
 * =========================================================
 * FUNÇÕES AUXILIARES
 * =========================================================
 */

const normalizarTexto = (valor = "") =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const obterIdEtapa = (etapa) => {
  if (typeof etapa === "object" && etapa !== null) {
    return etapa.id;
  }

  return etapa;
};

const obterNomeEtapa = (etapa, etapasCatalogo) => {
  if (typeof etapa === "object" && etapa !== null && etapa.nome) {
    return etapa.nome;
  }

  const etapaEncontrada = etapasCatalogo.find(
    (item) => String(item.id) === String(obterIdEtapa(etapa)),
  );

  return etapaEncontrada?.nome || "";
};

const obterEtapasModalidadesModelo = (modelo) => {
  const itens = [
    ...(Array.isArray(modelo.etapas) ? modelo.etapas : []),

    ...(Array.isArray(modelo.modalidades) ? modelo.modalidades : []),
  ];

  return [
    ...new Map(
      itens.map((item) => [String(obterIdEtapa(item)), item]),
    ).values(),
  ];
};

/*
 * Verifica se os chips cabem
 * em no máximo duas linhas.
 */
const cabeEmDuasLinhas = (larguras, larguraDisponivel, larguraExtra = 0) => {
  if (larguraDisponivel <= 0) {
    return false;
  }

  const itens = [...larguras];

  if (larguraExtra > 0) {
    itens.push(larguraExtra);
  }

  let linha = 1;

  let larguraUsada = 0;

  for (const larguraItem of itens) {
    if (larguraUsada === 0) {
      larguraUsada = larguraItem;

      continue;
    }

    if (larguraUsada + GAP_CHIPS_PX + larguraItem <= larguraDisponivel) {
      larguraUsada += GAP_CHIPS_PX + larguraItem;

      continue;
    }

    linha += 1;

    if (linha > 2) {
      return false;
    }

    larguraUsada = larguraItem;
  }

  return true;
};

/*
 * =========================================================
 * RÓTULO DOS FILTROS
 * =========================================================
 */

const RotuloCampo = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",

      mb: 0.55,

      color: "var(--sear-texto-secundario, #4d6484)",

      fontSize: ajustarTamanhoFonteSre("0.72rem"),

      fontWeight: 600,
    }}
  >
    {children}
  </Typography>
);

/*
 * =========================================================
 * FILTRO MÚLTIPLO
 * =========================================================
 */

const FiltroMultiplo = ({
  placeholder,
  itens,
  selecionados,
  onChange,
  icone,
}) => {
  const nomesPorId = useMemo(
    () => new Map(itens.map((item) => [String(item.id), item.nome])),

    [itens],
  );

  const handleChange = (event) => {
    const valor = event.target.value;

    onChange(typeof valor === "string" ? valor.split(",") : valor);
  };

  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        minWidth: 0,

        width: "100%",
      }}
    >
      <Select
        multiple
        displayEmpty
        value={selecionados}
        onChange={handleChange}
        input={
          <OutlinedInput
            startAdornment={
              icone ? (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      color: "var(--sear-azul, #0767d8)",
                    }}
                  >
                    {icone}
                  </Box>
                </InputAdornment>
              ) : null
            }
            sx={{
              minWidth: 0,
            }}
          />
        }
        renderValue={(valores) => {
          if (!Array.isArray(valores) || valores.length === 0) {
            return (
              <Typography
                component="span"
                variant="body2"
                noWrap
                sx={{
                  display: "block",

                  width: "100%",

                  minWidth: 0,

                  overflow: "hidden",

                  textOverflow: "ellipsis",

                  whiteSpace: "nowrap",

                  color: "var(--sear-texto-secundario, #52606d)",

                  fontSize: ajustarTamanhoFonteSre("0.82rem"),
                }}
              >
                {placeholder}
              </Typography>
            );
          }

          const nomes = valores
            .map((id) => nomesPorId.get(String(id)))
            .filter(Boolean);

          const textoSelecionado = nomes.join(", ");

          return (
            <Tooltip title={textoSelecionado} arrow describeChild>
              <Typography
                component="span"
                variant="body2"
                
                sx={{
                  display: "block",
  
                  width: "100%",
  
                  maxWidth: "100%",
  
                  minWidth: 0,
  
                  overflow: "hidden",
  
                  textOverflow: "ellipsis",
  
                  whiteSpace: "nowrap",
  
                  color: "var(--sear-texto, #37474f)",
  
                  fontSize: ajustarTamanhoFonteSre("0.82rem"),
                }}
              >
                {textoSelecionado}
              </Typography>
            </Tooltip>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 320,
              maxWidth: "calc(100vw - 32px)",
              mt: 0.75,
              border: "1px solid var(--sear-borda, #d6e3f3)",
              borderRadius: 1.5,
              boxShadow: "0 5px 16px #20446b14",
            },
          },
          MenuListProps: { sx: { p: 0.5 } },
        }}
        sx={{
          width: "100%",

          minWidth: 0,

          backgroundColor: "var(--sear-superficie, #fff)",

          "& .MuiSelect-select": {
            minWidth: 0,

            maxWidth: "100%",

            py: 1,

            overflow: "hidden",

            textOverflow: "ellipsis",

            whiteSpace: "nowrap",
          },
        }}
      >
        {itens.map((item) => {
          const id = String(item.id);

          const selecionado = selecionados.includes(id);

          return (
            <MenuItem
              key={item.id}
              value={id}
              sx={{
                whiteSpace: "normal",
                minHeight: 42,
                px: 1.5,
                py: 1,
                my: 0.25,
                gap: 1.5,
                borderRadius: 1,
                color: "var(--sear-azul, #24458b)",
                "&:hover": { bgcolor: "var(--sear-superficie-azul, #eef6ff)" },
                "&.Mui-selected": {
                  bgcolor: "var(--sear-superficie-azul, #eaf4ff)",
                  color: "var(--sear-azul, #0967ca)",
                  "&:hover, &.Mui-focusVisible": { bgcolor: "var(--sear-superficie-azul, #dceeff)" },
                },
              }}
            >
              <ListItemText
                primary={item.nome}
                sx={{ m: 0, minWidth: 0, overflowWrap: "anywhere" }}
                primaryTypographyProps={{
                  fontSize: ajustarTamanhoFonteSre("13px"),
                  fontWeight: selecionado ? 600 : 400,
                  lineHeight: 1.45,
                }}
              />
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: 22,
                  height: 22,
                  color: "var(--sear-azul, #087cf0)",
                  visibility: selecionado ? "visible" : "hidden",
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

/*
 * =========================================================
 * ETAPAS / MODALIDADES COMPACTAS
 * =========================================================
 *
 * Mostra tudo o que couber em
 * até duas linhas.
 *
 * O restante fica no chip +N.
 */

const EtapasCompactas = ({ modelo, etapasCatalogo }) => {
  const containerRef = useRef(null);

  const medicaoRef = useRef(null);

  const [quantidadeVisivel, setQuantidadeVisivel] = useState(0);

  const itens = useMemo(
    () =>
      obterEtapasModalidadesModelo(modelo)
        .map((item) => ({
          id: obterIdEtapa(item),

          nome:
            obterNomeEtapa(item, etapasCatalogo) ||
            "Etapa/modalidade não identificada",
        }))
        .sort((a, b) =>
          String(a.nome).localeCompare(String(b.nome), "pt-BR", {
            sensitivity: "base",

            numeric: true,
          }),
        ),

    [modelo, etapasCatalogo],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    const areaMedicao = medicaoRef.current;

    if (!container || !areaMedicao) {
      return undefined;
    }

    let quadro = null;

    const calcular = () => {
      if (quadro) {
        cancelAnimationFrame(quadro);
      }

      quadro = requestAnimationFrame(() => {
        const larguraDisponivel = container.clientWidth;

        if (!larguraDisponivel || itens.length === 0) {
          setQuantidadeVisivel(itens.length);

          return;
        }

        const elementosChips = areaMedicao.querySelectorAll(
          '[data-etapa-medicao="true"]',
        );

        const chipMais = areaMedicao.querySelector(
          '[data-etapa-excedente="true"]',
        );

        const larguras = Array.from(elementosChips).map((elemento) =>
          Math.ceil(elemento.getBoundingClientRect().width),
        );

        const larguraMais = chipMais
          ? Math.ceil(chipMais.getBoundingClientRect().width)
          : 0;

        /*
         * Todos os chips cabem.
         */
        if (cabeEmDuasLinhas(larguras, larguraDisponivel)) {
          setQuantidadeVisivel(itens.length);

          return;
        }

        /*
         * Reserva espaço
         * para o chip +N.
         */
        let visiveis = itens.length - 1;

        while (visiveis > 0) {
          const largurasVisiveis = larguras.slice(0, visiveis);

          if (
            cabeEmDuasLinhas(largurasVisiveis, larguraDisponivel, larguraMais)
          ) {
            break;
          }

          visiveis -= 1;
        }

        setQuantidadeVisivel(visiveis);
      });
    };

    calcular();

    let observer = null;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(calcular);

      observer.observe(container);
    } else {
      window.addEventListener("resize", calcular);
    }

    return () => {
      if (quadro) {
        cancelAnimationFrame(quadro);
      }

      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", calcular);
      }
    };
  }, [itens]);

  if (itens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Não informado
      </Typography>
    );
  }

  const itensVisiveis = itens.slice(0, quantidadeVisivel);

  const itensOcultos = itens.slice(quantidadeVisivel);

  return (
    <Box
      sx={{
        position: "relative",

        width: "100%",

        minWidth: 0,
      }}
    >
      {/* ÁREA VISÍVEL */}

      <Box
        ref={containerRef}
        sx={{
          display: "flex",

          flexWrap: "wrap",

          alignItems: "center",

          gap: `${GAP_CHIPS_PX}px`,

          width: "100%",

          minWidth: 0,
        }}
      >
        {itensVisiveis.map((item) => (
          <Chip
            key={item.id}
            size="small"
            label={item.nome}
            sx={ESTILO_CHIP_ETAPA}
          />
        ))}

        {itensOcultos.length > 0 && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={250}
            title={
              <Box
                sx={{
                  py: 0.5,

                  px: 0.25,

                  maxWidth: 360,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",

                    mb: 0.75,

                    color: "#fff",

                    fontWeight: 700,
                  }}
                >
                  Demais etapas/modalidades ({itensOcultos.length})
                </Typography>

                {itensOcultos.map((item) => (
                  <Typography
                    key={item.id}
                    variant="caption"
                    sx={{
                      display: "block",

                      color: "rgba(255,255,255,0.92)",

                      lineHeight: 1.5,
                    }}
                  >
                    {item.nome}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Chip
              size="small"
              label={`+${itensOcultos.length}`}
              sx={{
                height: 23,

                backgroundColor: "var(--sear-superficie-azul, #e3f2fd)",

                color: "var(--sear-azul, #0767d8)",

                fontSize: ajustarTamanhoFonteSre("0.7rem"),

                fontWeight: 700,

                cursor: "default",

                "& .MuiChip-label": {
                  px: 1,
                },

                "&:hover": {
                  backgroundColor: "var(--sear-superficie-azul, #d7ebff)",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* ÁREA INVISÍVEL PARA MEDIR OS CHIPS */}

      <Box
        ref={medicaoRef}
        aria-hidden="true"
        sx={{
          position: "fixed",

          left: -10000,

          top: -10000,

          display: "flex",

          alignItems: "center",

          visibility: "hidden",

          pointerEvents: "none",

          whiteSpace: "nowrap",
        }}
      >
        {itens.map((item) => (
          <Chip
            key={`medicao-${item.id}`}
            data-etapa-medicao="true"
            size="small"
            label={item.nome}
            sx={ESTILO_CHIP_ETAPA}
          />
        ))}

        <Chip
          data-etapa-excedente="true"
          size="small"
          label={`+${itens.length}`}
          sx={{
            height: 23,

            backgroundColor: "var(--sear-superficie-azul, #e3f2fd)",

            color: "var(--sear-azul, #0767d8)",

            fontSize: ajustarTamanhoFonteSre("0.7rem"),

            fontWeight: 700,

            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

/*
 * =========================================================
 * COMPONENTE PRINCIPAL
 * =========================================================
 */

const CatalogoModelosLista = ({
  isSuperintendent,
  filtrosLaterais = false,
  modelos,
  categorias,
  assuntos,
  etapasModalidades = [],
  onDownload,
  onEditar,
  onExcluir,
}) => {
  /*
   * =======================================================
   * FILTROS
   * =======================================================
   */

  const [busca, setBusca] = useState("");

  const [categoriasFiltro, setCategoriasFiltro] = useState([]);

  const [assuntosFiltro, setAssuntosFiltro] = useState([]);

  const [etapasFiltro, setEtapasFiltro] = useState([]);

  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);
  const camposFiltrosId = useId();
  const filtrosNoFluxo = filtrosExpandidos && !filtrosLaterais;
  // Mantém os mesmos campos: só o Analista usa o painel sobreposto.
  const ContainerCamposFiltros = filtrosLaterais ? Drawer : React.Fragment;

  /*
   * =======================================================
   * PAGINAÇÃO
   * =======================================================
   */

  const [pagina, setPagina] = useState(1);

  const [itensPorPagina, setItensPorPagina] = useState(5);

  /*
   * =======================================================
   * ORDENAÇÃO
   * =======================================================
   */

  const [ordem, setOrdem] = useState({
    coluna: null,

    direcao: "asc",
  });

  /*
   * =======================================================
   * DISTRIBUIÇÃO DAS COLUNAS POR PERFIL
   * =======================================================
   */

  const gridColunasModelos = isSuperintendent
    ? GRID_COLUNAS_SRE
    : GRID_COLUNAS_CONSULTA;

  const comparar = useMemo(
    () =>
      new Intl.Collator("pt-BR", {
        sensitivity: "base",

        numeric: true,
      }),

    [],
  );

  /*
   * =======================================================
   * ETAPAS / MODALIDADES
   * =======================================================
   */

  const etapas = useMemo(
    () =>
      [...etapasModalidades].sort((a, b) =>
        String(a.nome ?? "").localeCompare(String(b.nome ?? ""), "pt-BR"),
      ),

    [etapasModalidades],
  );

  /*
   * =======================================================
   * FILTRAGEM
   * =======================================================
   */

  const modelosFiltrados = useMemo(() => {
    const termo = normalizarTexto(busca);

    return modelos.filter((modelo) => {
      const itensModelo = obterEtapasModalidadesModelo(modelo);

      const nomesEtapas = itensModelo
        .map((item) => obterNomeEtapa(item, etapas))
        .join(" ");

      const correspondeBusca =
        !termo ||
        normalizarTexto(modelo.titulo).includes(termo) ||
        normalizarTexto(modelo.nome_original).includes(termo) ||
        normalizarTexto(modelo.categoria_nome).includes(termo) ||
        normalizarTexto(modelo.assunto_nome).includes(termo) ||
        normalizarTexto(nomesEtapas).includes(termo);

      const correspondeCategoria =
        categoriasFiltro.length === 0 ||
        categoriasFiltro.includes(String(modelo.categoria_id));

      const correspondeAssunto =
        assuntosFiltro.length === 0 ||
        assuntosFiltro.includes(String(modelo.assunto_id ?? ""));

      const idsEtapasModelo = itensModelo.map((item) =>
        String(obterIdEtapa(item)),
      );

      const correspondeEtapa =
        etapasFiltro.length === 0 ||
        etapasFiltro.some((etapaId) =>
          idsEtapasModelo.includes(String(etapaId)),
        );

      return (
        correspondeBusca &&
        correspondeCategoria &&
        correspondeAssunto &&
        correspondeEtapa
      );
    });
  }, [modelos, busca, categoriasFiltro, assuntosFiltro, etapasFiltro, etapas]);

  /*
   * =======================================================
   * ORDENAÇÃO
   * =======================================================
   */

  const modelosOrdenados = useMemo(() => {
    if (!ordem.coluna) {
      return modelosFiltrados;
    }

    const obterValorOrdenacao = (modelo) => {
      if (ordem.coluna === "titulo") {
        return modelo.titulo || "";
      }

      if (ordem.coluna === "categoria") {
        return modelo.categoria_nome || "";
      }

      if (ordem.coluna === "assunto") {
        return modelo.assunto_nome || "";
      }

      return "";
    };

    return [...modelosFiltrados].sort((a, b) => {
      const valorA = obterValorOrdenacao(a);

      const valorB = obterValorOrdenacao(b);

      let resultado = comparar.compare(valorA, valorB);

      if (resultado === 0) {
        resultado = comparar.compare(
          String(a.id ?? ""),

          String(b.id ?? ""),
        );
      }

      return ordem.direcao === "asc" ? resultado : -resultado;
    });
  }, [modelosFiltrados, ordem, comparar]);

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,

      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPagina(1);
  };

  /*
   * =======================================================
   * CÁLCULO DA PAGINAÇÃO
   * =======================================================
   */

  const totalPaginas = Math.max(
    1,

    Math.ceil(modelosOrdenados.length / itensPorPagina),
  );

  const paginaAtual = Math.min(pagina, totalPaginas);

  const indiceInicial = (paginaAtual - 1) * itensPorPagina;

  const indiceFinal = Math.min(
    indiceInicial + itensPorPagina,

    modelosOrdenados.length,
  );

  const modelosPagina = modelosOrdenados.slice(indiceInicial, indiceFinal);

  /*
   * Volta para a página 1
   * sempre que filtros ou
   * quantidade por página mudarem.
   */
  useEffect(() => {
    setPagina(1);
  }, [busca, categoriasFiltro, assuntosFiltro, etapasFiltro, itensPorPagina]);

  /*
   * Impede uma página inválida
   * após redução da quantidade
   * de resultados.
   */
  useEffect(() => {
    if (pagina > totalPaginas) {
      setPagina(totalPaginas);
    }
  }, [pagina, totalPaginas]);

  /*
   * =======================================================
   * LIMPAR FILTROS
   * =======================================================
   */

  const limparFiltros = () => {
    setBusca("");

    setCategoriasFiltro([]);

    setAssuntosFiltro([]);

    setEtapasFiltro([]);

    setPagina(1);
  };

  const filtrosAtivos =
    Boolean(busca.trim()) ||
    categoriasFiltro.length > 0 ||
    assuntosFiltro.length > 0 ||
    etapasFiltro.length > 0;

  const resumoFiltros = useMemo(() => {
    const resumo = busca.trim() ? [`Busca: ${busca.trim()}`] : [];

    [
      ["Categoria", categoriasFiltro, categorias],
      ["Assunto", assuntosFiltro, assuntos],
      ["Etapa/modalidade", etapasFiltro, etapas],
    ].forEach(([rotulo, selecionados, itens]) => {
      if (selecionados.length === 0) return;

      const nomesPorId = new Map(
        itens.map((item) => [String(item.id), item.nome]),
      );
      const nomes = selecionados.map((id) => nomesPorId.get(String(id))).filter(Boolean);
      resumo.push(`${rotulo}: ${nomes.join(", ") || `${selecionados.length} selecionado(s)`}`);
    });

    return resumo.join(" · ") || "Nenhum filtro aplicado";
  }, [busca, categoriasFiltro, categorias, assuntosFiltro, assuntos, etapasFiltro, etapas]);

  /*
   * =======================================================
   * CABEÇALHO ORDENÁVEL
   * =======================================================
   */

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          width: "fit-content",

          color: coresTextosGlobais.cabecalhoTabela,

          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,

          fontWeight: 700,

          "&:hover": {
            color: "var(--sear-azul, #0767d8)",
          },

          "&.Mui-active": {
            color: coresTextosGlobais.cabecalhoTabela,
          },

          "& .MuiTableSortLabel-icon": {
            fontSize: 15,

            opacity: 1,

            color: "var(--sear-azul, #0767d8)",

            transform: "none !important",

            ml: 0.35,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",

            alignItems: "center",

            gap: 0.65,

            whiteSpace: "nowrap",
          }}
        >
          <Icone
            sx={{
              fontSize: 17,

              color: "var(--sear-azul, #087cf0)",

              flexShrink: 0,
            }}
          />

          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  /*
   * =======================================================
   * CABEÇALHO NÃO ORDENÁVEL
   * =======================================================
   */

  const renderCabecalhoSimples = (titulo, Icone, centralizado = false) => (
    <Box
      sx={{
        display: "flex",

        alignItems: "center",

        justifyContent: centralizado ? "center" : "flex-start",

        gap: 0.65,

        minWidth: 0,

        width: "100%",
      }}
    >
      <Icone
        sx={{
          fontSize: 17,

          color: "var(--sear-azul, #087cf0)",

          flexShrink: 0,
        }}
      />

      <Typography
        variant="caption"
        sx={{
          color: coresTextosGlobais.cabecalhoTabela,

          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,

          fontWeight: 700,

          whiteSpace: "nowrap",
        }}
      >
        {titulo}
      </Typography>
    </Box>
  );

  /*
   * SRE possui três ações.
   * Analista / Escola possuem
   * apenas download.
   */
  const tituloUltimaColuna = isSuperintendent ? "Ações" : "Baixar";

  const IconeUltimaColuna = isSuperintendent
    ? SettingsRoundedIcon
    : DownloadRoundedIcon;

  return (
    <Box
      sx={{
        width: "100%",
        display: { md: "flex" },
        flexDirection: { md: "column" },
        flex: { md: "1 1 0" },
        minHeight: { md: 0 },
        overflow: { md: "hidden" },
      }}
    >
      {/* =====================================================
          FILTROS
      ===================================================== */}

      <Box
        component="section"
        aria-label="Filtros do catálogo de modelos"
        sx={{
          border: "1px solid var(--sear-borda, #d7e7f7)",

          borderRadius: 2,

          backgroundColor: "var(--sear-superficie-azul, #f7fbff)",

          overflow: "hidden",
          flexShrink: 0,
          display: filtrosNoFluxo ? "block" : "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            px: filtrosNoFluxo ? {
              xs: 2,

              md: 2.25,
            } : 1.75,

            pt: filtrosNoFluxo ? 1.6 : 1,

            pb: filtrosNoFluxo ? 1.5 : 1,
            ...(!filtrosNoFluxo && {
              flex: "1 1 310px",
              "@media (max-width:1080px)": { flexBasis: "100%" },
            }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: filtrosLaterais ? 1.5 : 1.25, minWidth: 0 }}>
            {!filtrosLaterais && <Typography
              component="span"
              sx={{ flexShrink: 0, color: "var(--sear-azul, #24458b)", fontSize: ajustarTamanhoFonteSre("0.75rem"), fontWeight: 600 }}
            >
              Filtros
            </Typography>}
            <Tooltip title={filtrosLaterais ? "" : resumoFiltros} arrow describeChild>
              <Typography
                id={`${camposFiltrosId}-orientacao`}
                component="span"
                noWrap={!filtrosLaterais || filtrosAtivos}
                aria-live="polite"
                sx={{ flex: "1 1 auto", minWidth: 0, color: "var(--sear-texto-secundario, #52677f)", fontSize: ajustarTamanhoFonteSre("0.75rem") }}
              >
                {filtrosLaterais && !filtrosAtivos
                  ? "Para buscar um documento, clique em Filtros."
                  : resumoFiltros}
              </Typography>
            </Tooltip>
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={filtrosLaterais ? <FilterAltOutlinedIcon /> : undefined}
              aria-haspopup={filtrosLaterais ? "dialog" : undefined}
              aria-expanded={filtrosExpandidos}
              aria-describedby={filtrosLaterais ? `${camposFiltrosId}-orientacao` : undefined}
              aria-controls={filtrosLaterais ? (filtrosExpandidos ? `${camposFiltrosId}-painel` : undefined) : camposFiltrosId}
              onClick={() => setFiltrosExpandidos((atual) => !atual)}
              sx={{
                flexShrink: 0,
                order: filtrosLaterais ? -1 : undefined,
                minHeight: 32,
                px: filtrosLaterais ? 1.5 : 1.25,
                py: 0.5,
                borderRadius: 1,
                borderColor: "var(--sear-borda, #9fcdff)",
                color: "var(--sear-azul, #0767d8)",
                fontSize: ajustarTamanhoFonteSre(filtrosLaterais ? "0.78rem" : "0.75rem"),
                fontWeight: filtrosLaterais ? 600 : 500,
                ...(filtrosLaterais && {
                  backgroundColor: "var(--sear-superficie, #fff)",
                  "& .MuiButton-startIcon > svg": { fontSize: 18 },
                }),
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "var(--sear-borda-forte, #087cf0)", backgroundColor: "var(--sear-superficie-azul, #eef7ff)" },
              }}
            >
              {filtrosLaterais ? "Filtros" : filtrosExpandidos ? "Recolher filtros" : "Editar filtros"}
            </Button>
          </Box>

          {/* CAMPOS DOS FILTROS */}

          <ContainerCamposFiltros
            {...(filtrosLaterais ? {
              anchor: "right",
              open: filtrosExpandidos,
              onClose: () => setFiltrosExpandidos(false),
              ModalProps: {
                keepMounted: true,
                BackdropProps: { sx: { backgroundColor: "rgba(20, 43, 73, 0.2)" } },
              },
              PaperProps: {
                id: `${camposFiltrosId}-painel`,
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": `${camposFiltrosId}-titulo`,
                sx: {
                  width: { xs: "100%", sm: 400 },
                  maxWidth: "100vw",
                  height: "100dvh",
                  backgroundColor: "var(--sear-superficie, #fff)",
                  boxSizing: "border-box",
                  borderLeft: "1px solid var(--sear-borda, #d7e7f7)",
                  boxShadow: "-8px 0 32px rgba(23, 59, 114, 0.14)",
                  display: "flex",
                  flexDirection: "column",
                },
              },
            } : {})}
          >
            {filtrosLaterais && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 2.25, borderBottom: "1px solid var(--sear-borda, #d7e7f7)", flexShrink: 0 }}>
                <Box sx={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 1.5, backgroundColor: "var(--sear-superficie-azul, #eaf4ff)", color: "var(--sear-azul, #087cf0)", flexShrink: 0 }}>
                  <FilterAltOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography id={`${camposFiltrosId}-titulo`} component="h2" sx={{ color: coresTextosGlobais.tituloSecao, fontWeight: 700, fontSize: tamanhosPaginasAdministrativas.tituloSecao, lineHeight: 1.4 }}>
                    Filtros
                  </Typography>
                  <Typography sx={{ color: "var(--sear-texto-secundario, #667b98)", fontSize: ajustarTamanhoFonteSre("0.78rem") }}>
                    Modelos e roteiros
                  </Typography>
                </Box>
                <IconButton aria-label="Fechar filtros" onClick={() => setFiltrosExpandidos(false)} sx={{ color: "var(--sear-texto-secundario, #52677f)" }}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
            )}
            <Box sx={filtrosLaterais ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto", px: 2.5, py: 2.5 } : undefined}>
              {filtrosLaterais && (
                <Typography sx={{ color: "var(--sear-texto-secundario, #667b98)", fontSize: ajustarTamanhoFonteSre("0.78rem"), lineHeight: 1.5, mb: 2.5 }}>
                  A lista é atualizada enquanto você filtra.
                </Typography>
              )}
          <Box
            id={camposFiltrosId}
            hidden={!filtrosExpandidos}
            sx={{
              display: filtrosExpandidos ? "grid" : "none",
              mt: filtrosLaterais ? 0 : 1.5,

              gridTemplateColumns: filtrosLaterais ? "minmax(0, 1fr)" : {
                xs: "minmax(0, 1fr)",

                sm: "minmax(0, 1fr) minmax(0, 1fr)",

                lg: "minmax(0, 1.25fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.95fr)",
              },

              gap: filtrosLaterais ? 2.5 : 1.3,

              width: "100%",

              minWidth: 0,
            }}
          >
            {/* BUSCAR MODELO */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Buscar modelo</RotuloCampo>

              <TextField
                size="small"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Digite o nome do modelo..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        fontSize="small"
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 0,

                  width: "100%",

                  backgroundColor: "var(--sear-superficie, #fff)",

                  "& .MuiInputBase-root": {
                    minWidth: 0,
                  },

                  "& .MuiInputBase-input": {
                    minWidth: 0,

                    fontSize: ajustarTamanhoFonteSre("0.82rem"),

                    py: 1,

                    overflow: "hidden",

                    textOverflow: "ellipsis",
                  },
                }}
              />
            </Box>

            {/* CATEGORIA */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Categoria</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todas as categorias"
                itens={categorias}
                selecionados={categoriasFiltro}
                onChange={setCategoriasFiltro}
                icone={
                  <LayersRoundedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>

            {/* ASSUNTO */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Assunto</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todos os assuntos"
                itens={assuntos}
                selecionados={assuntosFiltro}
                onChange={setAssuntosFiltro}
                icone={
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>

            {/* ETAPA / MODALIDADE */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Etapa/modalidade</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todas as etapas"
                itens={etapas}
                selecionados={etapasFiltro}
                onChange={setEtapasFiltro}
                icone={
                  <SchoolOutlinedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>
          </Box>
            </Box>
            {filtrosLaterais && (
              <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid var(--sear-borda, #d7e7f7)", backgroundColor: "var(--sear-superficie-azul, #f7fbff)", flexShrink: 0 }}>
                <Typography aria-live="polite" sx={{ mb: 1.25, color: "var(--sear-texto-secundario, #52677f)", fontSize: ajustarTamanhoFonteSre("0.78rem") }}>
                  <Box component="span" sx={{ color: "var(--sear-azul, #0767d8)", fontWeight: 700 }}>{modelosFiltrados.length}</Box>{" "}
                  {modelosFiltrados.length === 1 ? "registro encontrado" : "registros encontrados"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Button type="button" startIcon={<RestartAltRoundedIcon />} onClick={limparFiltros} disabled={!filtrosAtivos} sx={{ color: "var(--sear-azul, #0767d8)", fontSize: ajustarTamanhoFonteSre("0.78rem"), textTransform: "none", px: 0.75 }}>
                    Limpar filtros
                  </Button>
                  <Button type="button" variant="contained" disableElevation onClick={() => setFiltrosExpandidos(false)} sx={{ backgroundColor: "var(--sear-acao-azul, #087cf0)", fontSize: ajustarTamanhoFonteSre("0.78rem"), fontWeight: 600, textTransform: "none", px: 2, minHeight: 36 }}>
                    Ver resultados
                  </Button>
                </Box>
              </Box>
            )}
          </ContainerCamposFiltros>
        </Box>

        {/* =====================================================
            FAIXA INFERIOR DOS FILTROS
        ===================================================== */}

        <Box
          sx={{
            display: "flex",

            alignItems: {
              xs: "flex-start",

              md: "center",
            },

            justifyContent: "space-between",

            flexWrap: "wrap",

            flexDirection: {
              xs: "column",

              md: "row",
            },

            gap: 1.25,

            px: filtrosNoFluxo ? {
              xs: 2,

              md: 2.25,
            } : 1.75,

            py: filtrosNoFluxo ? 1.05 : 1,

            borderTop: filtrosNoFluxo ? "1px solid var(--sear-borda, #dbe9f6)" : 0,

            backgroundColor: filtrosNoFluxo ? "var(--sear-superficie-azul, #f4f9fe)" : "transparent",
            ...(!filtrosNoFluxo && {
              flex: "0 0 auto",
              maxWidth: "100%",
              borderLeft: "1px solid var(--sear-borda, #dbe9f6)",
              "@media (max-width:1080px)": {
                flex: "1 1 auto",
                borderLeft: 0,
                borderTop: "1px solid var(--sear-borda, #dbe9f6)",
              },
            }),
          }}
        >
          {/* TOTAL DE REGISTROS */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              minWidth: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--sear-texto-secundario, #667b98)",
                fontSize: ajustarTamanhoFonteSre("0.72rem"),
              }}
            >
              <Box
                component="span"
                sx={{ color: "var(--sear-azul, #0767d8)", fontWeight: 700 }}
              >
                {modelosFiltrados.length}
              </Box>{" "}
              {modelosFiltrados.length === 1
                ? "registro encontrado"
                : "registros encontrados"}
            </Typography>

            <Button
              type="button"
              size="small"
              variant="text"
              startIcon={<RestartAltRoundedIcon />}
              onClick={limparFiltros}
              disabled={!filtrosAtivos}
              sx={{
                minWidth: 0,
                minHeight: 32,
                px: 0.75,
                py: 0.5,
                borderRadius: 1,
                color: "var(--sear-azul, #0767d8)",
                fontSize: ajustarTamanhoFonteSre("0.72rem"),
                fontWeight: 500,
                textTransform: "none",
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { ml: 0, mr: 0.5 },
                "& .MuiButton-startIcon > svg": { fontSize: 16 },
                "&:hover": { backgroundColor: "var(--sear-superficie-azul, #dceeff)" },
              }}
            >
              Limpar filtros
            </Button>
          </Box>

          {/* CONTROLES */}

          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              justifyContent: "flex-end",

              flexWrap: "wrap",

              gap: 1.25,

              ml: {
                md: "auto",
              },
            }}
          >
            {/* REGISTROS POR PÁGINA */}

            <Box
              sx={{
                display: "flex",

                alignItems: "center",

                gap: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "var(--sear-texto-secundario, #667b98)",

                  fontSize: ajustarTamanhoFonteSre("0.7rem"),

                  whiteSpace: "nowrap",
                }}
              >
                Registros por página:
              </Typography>

              <Select
                size="small"
                value={itensPorPagina}
                onChange={(event) =>
                  setItensPorPagina(Number(event.target.value))
                }
                renderValue={(valor) => `${valor} por página`}
                sx={{
                  minWidth: 118,

                  height: 32,

                  backgroundColor: "var(--sear-superficie, #fff)",

                  fontSize: ajustarTamanhoFonteSre("0.72rem"),

                  fontWeight: 500,

                  color: "var(--sear-texto-secundario, #3f5576)",

                  borderRadius: 1,

                  "& .MuiSelect-select": {
                    py: 0.65,
                  },
                }}
              >
                {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
                  <MenuItem key={quantidade} value={quantidade}>
                    {quantidade} por página
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* PAGINAÇÃO SUPERIOR GLOBAL */}

            <Box sx={{ ...separadorPaginacao, display: filtrosNoFluxo ? undefined : "none" }}>
              <PaginacaoLista
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                ariaLabel="Paginação superior do catálogo de modelos"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* =====================================================
          MODELOS CADASTRADOS
      ===================================================== */}

      <Box
        data-rolagem-conteudo={filtrosLaterais ? "" : undefined}
        sx={{
          mt: 1.5,
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 2,
          backgroundColor: "var(--sear-superficie, #fff)",
          overflow: filtrosLaterais ? { xs: "hidden", md: "auto" } : "hidden",
          display: { md: filtrosLaterais ? "block" : "flex" },
          flexDirection: { md: "column" },
          flex: { md: "1 1 0" },
          minHeight: { md: 0 },
        }}
      >
        {/* CABEÇALHO DO CARD */}

        <Box
          sx={{
            display: "flex",

            alignItems: "center",

            gap: 1,

            px: 2,

            py: 1.15,

            backgroundColor: "var(--sear-superficie-azul, #f7fbff)",

            borderBottom: "1px solid var(--sear-borda, #d7e7f7)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: coresTextosGlobais.tituloSecao,
              fontSize: tamanhosPaginasAdministrativas.tituloSecao,

              fontWeight: 700,
            }}
          >
            Modelos cadastrados
          </Typography>

          <Chip
            size="small"
            label={modelosFiltrados.length}
            sx={{
              height: 21,

              backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",

              color: "var(--sear-azul, #0767d8)",

              fontSize: ajustarTamanhoFonteSre("0.68rem"),

              fontWeight: 700,
            }}
          />
        </Box>

        {/* =====================================================
            TABELA
        ===================================================== */}

        <Box
          data-rolagem-conteudo={filtrosLaterais ? undefined : ""}
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Box
            sx={{
              minWidth: 980,
            }}
          >
            {/* CABEÇALHO DA TABELA */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridColunasModelos,
                width: "100%",
                minHeight: 46,
                alignItems: "center",
                backgroundColor: "var(--sear-superficie-azul, #f1f7fd)",
                borderBottom: "1px solid var(--sear-borda, #d7e7f7)",
                position: { md: filtrosLaterais ? "static" : "sticky" },
                top: { md: 0 },
                zIndex: { md: 1 },
              }}
            >
              {/* NOME */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid var(--sear-borda, #dfeaf5)",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Nome do modelo",

                  "titulo",

                  DescriptionOutlinedIcon,
                )}
              </Box>

              {/* CATEGORIA */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid var(--sear-borda, #dfeaf5)",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Categoria",

                  "categoria",

                  LayersRoundedIcon,
                )}
              </Box>

              {/* ASSUNTO */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid var(--sear-borda, #dfeaf5)",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Assunto",

                  "assunto",

                  DescriptionRoundedIcon,
                )}
              </Box>

              {/* ETAPA / MODALIDADE */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid var(--sear-borda, #dfeaf5)",
                }}
              >
                {renderCabecalhoSimples(
                  "Etapa/modalidade",

                  SchoolOutlinedIcon,
                )}
              </Box>

              {/* AÇÕES / BAIXAR */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 1,
                }}
              >
                {renderCabecalhoSimples(
                  tituloUltimaColuna,

                  IconeUltimaColuna,

                  true,
                )}
              </Box>
            </Box>

            {/* =================================================
                SEM RESULTADOS
            ================================================= */}

            {modelosFiltrados.length === 0 ? (
              <Box
                sx={{
                  px: 2,

                  py: 4,

                  textAlign: "center",
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    display: "inline-flex",

                    textAlign: "left",
                  }}
                >
                  Nenhum modelo encontrado com os filtros selecionados.
                </Alert>
              </Box>
            ) : (
              <>
                {/* =============================================
                    LINHAS
                ============================================= */}

                {modelosPagina.map((modelo, index) => (
                  <Box
                    key={modelo.id}
                    sx={{
                      display: "grid",

                      gridTemplateColumns: gridColunasModelos,

                      width: "100%",

                      minHeight: 70,

                      alignItems: "stretch",

                      borderTop: index === 0 ? "none" : "1px solid var(--sear-borda, #e6eef5)",

                      backgroundColor: "var(--sear-superficie, #fff)",

                      "&:hover": {
                        backgroundColor: "var(--sear-superficie, #fbfdff)",
                      },
                    }}
                  >
                    {/* NOME DO MODELO */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        gap: 1,

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid var(--sear-borda, #e6eef5)",
                      }}
                    >
                      <DescriptionRoundedIcon
                        fontSize="small"
                        sx={{
                          color: "var(--sear-azul, #087cf0)",

                          flexShrink: 0,
                        }}
                      />

                      <Tooltip title={modelo.titulo} arrow describeChild>
                        <Typography
                          variant="body2"
                          
                          sx={{
                            ...ESTILO_TEXTO_DUAS_LINHAS,
  
                            color: coresTextosGlobais.conteudoTabela,
  
                            fontWeight: 500,
  
                            fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                          }}
                        >
                          {modelo.titulo}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* CATEGORIA */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid var(--sear-borda, #e6eef5)",
                      }}
                    >
                      <Tooltip title={modelo.categoria_nome || ""} arrow describeChild>
                        <Chip
                          size="small"
                          label={modelo.categoria_nome || "—"}
                          
                          sx={{
                            minHeight: 24,
  
                            height: "auto",
  
                            maxWidth: "100%",
  
                            backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
  
                            color: "var(--sear-azul, #0767d8)",
  
                            fontSize: ajustarTamanhoFonteSre("0.72rem"),
  
                            "& .MuiChip-label": {
                              px: 1.1,
  
                              py: 0.15,
  
                              whiteSpace: "normal",
  
                              lineHeight: 1.25,
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>

                    {/* ASSUNTO */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid var(--sear-borda, #e6eef5)",
                      }}
                    >
                      <Tooltip title={modelo.assunto_nome || "Não informado"} arrow describeChild>
                        <Typography
                          variant="body2"
                          
                          
                          sx={{ color: coresTextosGlobais.conteudoTabela, 
                            ...ESTILO_TEXTO_DUAS_LINHAS,
  
                            fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                          }}
                        >
                          {modelo.assunto_nome || "Não informado"}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* ETAPA / MODALIDADE */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid var(--sear-borda, #e6eef5)",
                      }}
                    >
                      <EtapasCompactas
                        modelo={modelo}
                        etapasCatalogo={etapas}
                      />
                    </Box>

                    {/* AÇÕES / DOWNLOAD */}

                    <Box
                      sx={{
                        display: "flex",

                        justifyContent: "center",

                        alignItems: "center",

                        gap: 0.25,

                        minWidth: 0,

                        px: 0.5,

                        py: 1,
                      }}
                    >
                      {/* DOWNLOAD */}

                      <Tooltip title="Baixar" arrow>
                        <IconButton
                          size="small"
                          
                          aria-label="Baixar modelo"
                          onClick={() => onDownload(modelo)}
                          sx={ESTILO_ACAO_DOWNLOAD}
                        >
                          <DownloadRoundedIcon
                            sx={{
                              fontSize: 17,
                            }}
                          />
                        </IconButton>
                      </Tooltip>

                      {/* SRE: EDITAR / EXCLUIR */}

                      {isSuperintendent && (
                        <>
                          <Tooltip title="Editar" arrow>
                            <IconButton
                              size="small"
                              
                              aria-label="Editar modelo"
                              onClick={() => onEditar?.(modelo)}
                              sx={ESTILO_ACAO_EDITAR}
                            >
                              <EditRoundedIcon
                                sx={{
                                  fontSize: 17,
                                }}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Excluir" arrow>
                            <IconButton
                              size="small"
                              
                              aria-label="Excluir modelo"
                              onClick={() => onExcluir?.(modelo)}
                              sx={ESTILO_ACAO_EXCLUIR}
                            >
                              <DeleteOutlineRoundedIcon
                                sx={{
                                  fontSize: 17,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Box>

        {/* =====================================================
            PAGINAÇÃO INFERIOR
        ===================================================== */}

        {modelosFiltrados.length > 0 && (
          <Box
            sx={{
              display: "flex",

              alignItems: {
                xs: "flex-start",

                sm: "center",
              },

              justifyContent: "space-between",

              flexDirection: {
                xs: "column",

                sm: "row",
              },

              gap: 1,

              px: 2,

              py: 1,

              borderTop: "1px solid var(--sear-borda, #dbe9f6)",

              backgroundColor: "var(--sear-superficie-azul, #f9fbfd)",
            }}
          >
            {/* RESUMO */}

            <Typography
              variant="caption"
              sx={{
                color: "var(--sear-texto-secundario, #667b98)",

                fontSize: ajustarTamanhoFonteSre("0.7rem"),

                whiteSpace: "nowrap",
              }}
            >
              Exibindo{" "}
              <Box
                component="span"
                sx={{
                  color: "var(--sear-texto, #334e6f)",

                  fontWeight: 600,
                }}
              >
                {indiceInicial + 1}–{indiceFinal}
              </Box>{" "}
              de{" "}
              <Box
                component="span"
                sx={{
                  color: "var(--sear-texto, #334e6f)",

                  fontWeight: 600,
                }}
              >
                {modelosOrdenados.length}
              </Box>{" "}
              modelos
            </Typography>

            {/* PAGINAÇÃO INFERIOR GLOBAL */}

            <Box
              sx={{
                ml: {
                  sm: "auto",
                },
              }}
            >
              <PaginacaoLista
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                ariaLabel="Paginação inferior do catálogo de modelos"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CatalogoModelosLista;
